import React, { useEffect, useRef, useState } from 'react'
import { MiniMap, useReactFlow, type Node } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { CanvasMapViewport } from './CanvasMapViewport'
import { useOffscreenState } from './hooks/useOffscreenState'
import { canvasMapNodeColor } from './utils/canvasMap'
import { RESIZE_ANIMATION_MS } from './utils/canvasSplitDrag'
import type { CanvasToolbarSide } from './utils/canvasToolbarSide'
import './CanvasMap.scss'

/**
 * Wie lange ein Klick in der Karte fliegt.
 *
 * Dieselbe Zahl, mit der die **Tab-Reihenfolge** den Ausschnitt nachzieht
 * (`CanvasFlow`, `setCenter(…, { duration: 200 })`) — beide bringen einen an
 * eine andere Stelle desselben Canvas, und das soll sich gleich anfühlen.
 */
const FLIGHT_MS = 200

/**
 * Die Maße der Karte — HIER und nicht nur im Stylesheet.
 *
 * React Flow rechnet seine viewBox aus `style.width`/`style.height` und fällt
 * ohne sie auf 200×150 zurück (`MiniMapComponent`: `elementWidth = style?.width
 * ?? defaultWidth`). Steht die Größe nur im CSS, spannt die viewBox ein anderes
 * Seitenverhältnis auf als der Kasten, in den gezeichnet wird — der Inhalt wird
 * dann zentriert eingepasst, und links und rechts bleiben Streifen stehen, die
 * weder Schleier noch Rahmen erreichen. Genau daher kamen die zwei schwarzen
 * Senkrechten am 8. September 2026.
 *
 * Das Stylesheet nimmt dieselben Zahlen über die Variablen unten entgegen, statt
 * sie ein zweites Mal zu setzen.
 */
const MAP_WIDTH = 168
const MAP_HEIGHT = 112

/**
 * Die Farbe eines Nodes auf der Karte.
 *
 * Der Umweg über `unknown` ist React Flows Node-Typ geschuldet: `data` ist dort
 * ein offener Record, und die Karte bekommt ihre Nodes ungetypt gereicht. Ein
 * `as NoteNodeData` wäre kürzer und eine Behauptung — dies hier ist eine
 * Prüfung, und ein Node ohne Notiz bekommt schlicht die Vorgabefarbe.
 */
function colorOfNode(node: Node): string {
  const note = node.data?.note as { kind?: string; color?: string | null } | undefined
  return canvasMapNodeColor(note?.kind ?? 'note', note?.color ?? null)
}

export interface CanvasMapProps {
  /**
   * Auf welcher Seite die **Werkzeug-Pille** steht. Die Karte geht auf die
   * andere: Beide wollen sonst dieselbe untere Ecke.
   */
  toolbarSide?: CanvasToolbarSide
  /**
   * Alles weg, solange eine Geste läuft — dasselbe Muster wie `suppressAll`
   * bei den **Randpfeilen**. Ein Zettel, der unter die Karte getragen wird,
   * verschwände sonst dahinter.
   */
  suppressed?: boolean
}

/**
 * **Die Karte** — eine verkleinerte Abbildung des Canvas, mit dem
 * **Ausschnitt** als Rahmen darin (CONTEXT.md, `**Karte**`).
 *
 * **Ganz herausgezoomt ist sie immer da** — wer so weit hinausgeht, sucht
 * Übersicht. Sonst kommt sie, **wenn von keinem Element mehr etwas zu sehen ist** — wenn also keines ganz
 * im Bild steht und keines den Ausschnitt überspannt. Nicht schon, wenn eines
 * heraussteht, und auch nicht, wenn nur noch Zipfel zu sehen sind: Dafür sind
 * die **Randpfeile** da, und ein Zug genügt. Erst wenn
 * gar nichts mehr zu sehen ist, genügt kein Zug mehr, und dann sagt der Pfeil
 * zwar noch „dort", aber nicht mehr, wie weit.
 *
 * **Sie kommt sofort und geht verzögert.** Wer sie braucht, braucht sie jetzt;
 * verschwinden darf sie erst, wenn wieder etwas zu sehen ist und das eine
 * Weile geblieben ist. Ohne diese Unsymmetrie flatterte sie an der Grenze —
 * dasselbe Flattern, das beim **Drag Handle** zwei getrennte Schwellen
 * verhindern.
 *
 * **Sie bietet an, sie greift nicht ein** (ADR 0016): Sie holt niemanden
 * zurück, sie zeigt nur, wo es zurückginge.
 */
export const CanvasMap: React.FC<CanvasMapProps> = ({ toolbarSide = 'left', suppressed = false }) => {
  const { t } = useTranslation()
  const { setCenter, getZoom } = useReactFlow()
  const { anyVisible, hasElements, zoomedOut } = useOffscreenState()

  /*
    Zwei Wege zur Karte, und der zweite ist nicht der Notfall:

    - **Nichts mehr zu sehen** — man hat den Halt verloren. Ein Zipfel genügt,
      um sie fernzuhalten: Wer eine Zettelecke sieht, ist nicht verloren.
    - **Ganz herausgezoomt** — man SUCHT Übersicht, und dann ist sie immer da,
      gleich wo man steht (Sven, 2026-09-08). In dieser Lage ist meist alles im
      Bild; nach der ersten Regel allein käme sie also gerade dann nicht, wenn
      man ausdrücklich danach verlangt hat.

    Ohne Elemente in keinem der beiden Fälle: `anyVisible` wäre dann immer
    falsch, und zu zeigen gäbe es nichts.
  */
  const wanted = hasElements && (zoomedOut || !anyVisible) && !suppressed

  const [visible, setVisible] = useState(false)
  const leaveTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (wanted) {
      if (leaveTimerRef.current !== null) {
        window.clearTimeout(leaveTimerRef.current)
        leaveTimerRef.current = null
      }
      setVisible(true)
      return
    }
    // Nicht sofort weg: Wer an der Grenze hin- und herfährt, soll sie stehen
    // sehen statt blinken.
    if (leaveTimerRef.current !== null) return
    leaveTimerRef.current = window.setTimeout(() => {
      leaveTimerRef.current = null
      setVisible(false)
    }, RESIZE_ANIMATION_MS)
  }, [wanted])

  useEffect(() => () => {
    if (leaveTimerRef.current !== null) window.clearTimeout(leaveTimerRef.current)
  }, [])

  if (!visible) return null

  return (
    <div
      className={`canvas-map canvas-map--${toolbarSide === 'left' ? 'right' : 'left'}${wanted ? '' : ' canvas-map--leaving'}`}
      style={{ '--canvas-map-width': `${MAP_WIDTH}px`, '--canvas-map-height': `${MAP_HEIGHT}px` } as React.CSSProperties}
    >
      <MiniMap
        style={{ width: MAP_WIDTH, height: MAP_HEIGHT }}
        ariaLabel={t('projectCanvas.map.label')}
        pannable
        /*
          Kein `zoomable`: Der **Zoom** ist Sache des Nutzers (CONTEXT.md,
          `**Karte**`). Ein Rad über der Karte soll nichts anderes tun als ein
          Rad daneben.
        */
        zoomable={false}
        nodeColor={colorOfNode}
        nodeStrokeWidth={0}
        nodeBorderRadius={2}
        /*
          Fläche und Rahmen der Maske stehen im Stylesheet, nicht hier: React
          Flow reicht diese Props als CSS-Variablen durch, und die Regel dort
          braucht ohnehin `vector-effect` — sonst verschwindet der Rahmen (die
          Strichstärke zählt in User-Units einer viewBox, die über den ganzen
          Canvas spannt). Zwei Quellen für eine Farbe wären eine zu viel.
        */
        bgColor="transparent"
        onClick={(_event, position) => {
          // Der Zoom fährt bewusst mit, wie er ist: Ein Klick bringt einen
          // woandershin, er ändert nicht die Höhe.
          void setCenter(position.x, position.y, { zoom: getZoom() || 1, duration: FLIGHT_MS })
        }}
      />
      <CanvasMapViewport width={MAP_WIDTH} height={MAP_HEIGHT} />
    </div>
  )
}
